// This file defines and manages available experiments.
//
// It allows us to enable or disable features for testing purposes without deploying new code.
// The experiments are stored in localStorage, making them available even for logged-out users.
//
// Note that this page is intentionally available without authentication,
// so do not include sensitive information here, or use it to gate sensitive
// features.
//

import getLogger from 'utils/log/log'
import sentry from 'utils/sentry/sentry'

const log = getLogger('apiClient')

export enum ExperimentName {
  EnableApiExperimentUrl = 'enable_api_experiment_url',
  HoldbackApiExperimentUrl = 'holdback_api_experiment_url',
  EnableTranslationFiles = 'enable_translation_files',

  // Add new experiment names here and in the experimentDefinitions array below.
}

// Note that the /experiments page is shown to the public, so do
// not include sensitive information here.
export type ExperimentDefinition = {
  // A unique key for the experiment, used for storage and retrieval
  name: ExperimentName

  // A human-friendly title for the experiment, shown in the UI
  title: string

  // A description of the experiment, shown in the UI
  //
  description: string

  // The default value of the experiment, used when no value is stored.
  defaultValue: boolean

  // Read once while the app starts, so a change only takes effect after a reload.
  readAtStartup?: boolean
}

export const experimentDefinitions: ExperimentDefinition[] = [
  {
    // See https://app-eu.wrike.com/open.htm?id=4364240621
    name: ExperimentName.EnableApiExperimentUrl,
    title: 'API Experiment URL',
    description: 'Use the new experimental API backend instead of the default one.',
    defaultValue: false,
  },
  {
    // See https://app-eu.wrike.com/open.htm?id=4364240621
    name: ExperimentName.HoldbackApiExperimentUrl,
    title: 'API Holdback Experiment URL',
    description:
      'Use the old API backend instead of the default one. This wins if also enable_api_experiment_url is enabled.',
    defaultValue: false,
  },
  {
    name: ExperimentName.EnableTranslationFiles,
    title: 'Translations from files',
    description: 'Load the standard translations from the files shipped with the app instead of from Transifex.',
    defaultValue: false,
    readAtStartup: true,
  },
  // Add new experiments here together with a name above.
]

export type Experiments = Record<ExperimentName, boolean>

export const EXPERIMENTS_STORAGE_KEY = 'experiments'

// Cache to avoid repeatedly parsing localStorage
let experimentsCache: Experiments | null = null

function getDefaultExperiments(): Experiments {
  // Cache default experiments since this is called frequently
  const defaults = {} as Experiments
  for (const exp of experimentDefinitions) {
    defaults[exp.name] = exp.defaultValue
  }
  return defaults
}

/**
 * Invalidates the experiments cache. Exported for testing purposes.
 * @internal
 */
export function invalidateExperimentsCache(): void {
  experimentsCache = null
}

export function getExperiments(): Experiments {
  if (experimentsCache !== null) {
    return experimentsCache
  }

  const defaults = getDefaultExperiments()
  let storedValue: string | null = null

  try {
    storedValue = localStorage.getItem(EXPERIMENTS_STORAGE_KEY)
    if (storedValue) {
      experimentsCache = { ...defaults, ...JSON.parse(storedValue) }
      return experimentsCache ?? defaults
    }
  } catch (err: any) {
    // Invalid JSON, log warning and return defaults.
    log.warn('Failed to parse experiments from localStorage:', err)
    sentry.captureException(err, {
      level: 'warning',
      tags: {
        sentry_scope: 'experiments',
        issue_type: 'local_storage_parse_failure',
      },
      extra: {
        storageKey: EXPERIMENTS_STORAGE_KEY,
        storedValue,
      },
      fingerprint: ['experiments', 'local_storage_parse_failure'],
    })
  }
  experimentsCache = defaults
  return experimentsCache
}

function changedStartupExperiment(before: Experiments, after: Experiments): boolean {
  return experimentDefinitions.some(
    (experiment) => experiment.readAtStartup && before[experiment.name] !== after[experiment.name]
  )
}

/**
 * Stores an experiment's value and returns whether the page must reload for the change to take effect.
 */
export function setExperiment(key: ExperimentName, value: boolean): boolean {
  const before = { ...getExperiments() }
  localStorage.setItem(EXPERIMENTS_STORAGE_KEY, JSON.stringify({ ...before, [key]: value }))
  invalidateExperimentsCache()
  return changedStartupExperiment(before, getExperiments())
}

/**
 * Resets every experiment to its default and returns whether the page must reload for the change to take effect.
 */
export function resetExperiments(): boolean {
  const before = { ...getExperiments() }
  localStorage.removeItem(EXPERIMENTS_STORAGE_KEY)
  invalidateExperimentsCache()
  return changedStartupExperiment(before, getExperiments())
}

/**
 * Check if an experiment is enabled.
 *
 * Values are cached for performance, so this method can be called often.
 * @param experimentName
 * @returns True if experiment is enabled.
 */
export function isExperimentEnabled(experimentName: ExperimentName): boolean {
  return getExperiments()[experimentName]
}

/**
 * Check if enable_api_experiment_url experiment is enabled.
 */
export function isApiExperimentEnabled(): boolean {
  return isExperimentEnabled(ExperimentName.EnableApiExperimentUrl)
}

/**
 * Check if holdback_api_experiment_url experiment is enabled.
 */
export function isHoldbackApiExperimentEnabled(): boolean {
  return isExperimentEnabled(ExperimentName.HoldbackApiExperimentUrl)
}
